import { ActionReducerMapBuilder, createAsyncThunk } from "@reduxjs/toolkit";
import { doCampusAuth, login, LoginResponse } from "../Api/authentication";
import { RootState } from "../app/store";
import { ParametersState } from "../reduxSlices/parametersSlice";
import { StudentState } from "../reduxSlices/studentSlice";
import { EstadoLoginStudent } from "../types";
import { isExpectedLoginAuthError } from "../utils/authHelpers";

// Tipo local para el state de globalFlagsSlice (evita import circular ya que el builder
// se invoca desde el propio archivo del slice).
type GlobalFlagsLikeState = { isLogginProcess: boolean };

/**
 * Argumentos del thunk de login.
 */
export interface DoLoginThunkArgs {
  email: string;
  /**
   * Si es true y existe `campusToken` en localStorage, intenta primero `doCampusAuth`.
   * En caso de error, hace fallback a `login(email)` estándar.
   */
  useCampusTokenFallback?: boolean;
  /**
   * Cantidad máxima de reintentos al endpoint /doLogin si la respuesta no es 200.
   * Por defecto 0 (sin reintentos).
   */
  maxRetries?: number;
  /**
   * Delays (en ms) entre cada reintento. El index N se aplica antes del reintento N+1.
   * Si hay más reintentos que delays, se reutiliza el último valor.
   */
  retryDelays?: number[];
  /**
   * Si es true, aplica la lógica de persistencia de `idAlumnoPrograma` en localStorage:
   * - 1 sola carrera → siempre guarda.
   * - >1 carreras → guarda sólo si todavía no existía un idAP previo.
   */
  persistIdAP?: boolean;
}

/**
 * Resultado del thunk con la respuesta original más información derivada.
 */
export interface DoLoginThunkResult {
  data: EstadoLoginStudent;
  status: number;
  /** Nombre de carrera que corresponde mostrar como "carrera actual" según la respuesta. */
  nombreCarreraSeleccionada: string;
  /** idAlumnoPrograma derivado para persistir / mostrar (puede ser undefined si no hay carreras). */
  idAlumnoPrograma?: number;
  /** Si la autenticación fue resuelta vía campus token. */
  isCampus: boolean;
}

const wait = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

const buildEmptyLoginResponse = (errorMsg: any): LoginResponse => ({
  data: {
    bloqueado: false,
    cantidadCarreras: 0,
    carreras: [],
    debeElegirCarrera: false,
    inicializando: false,
    mensajeBloqueo: "",
    mensajePrioridad: "",
    contactoBloqueo: "",
    mostrarHorarios: false,
    prioridad: false,
    debeAlertarSuperposicion: false,
    filtrarCursosConCupoNoAsignado: false,
    token: "",
    error: errorMsg,
  },
  status: 500,
});

/**
 * Thunk centralizado para resolver el login del alumno.
 *
 * Orquesta:
 *  - Llamada a `doCampusAuth` (opcional, si hay `campusToken`) con fallback a `login`.
 *  - Reintentos configurables del endpoint estándar.
 *  - Persistencia de `token` e `idAlumnoPrograma` en localStorage.
 *  - Calcula `nombreCarreraSeleccionada` según la lógica de mono/multi-carrera.
 *
 * Las modificaciones del store se realizan en los `*Builder` exportados (un builder
 * por slice afectado) que cada slice debe registrar en su `extraReducers`.
 */
export const doLoginThunk = createAsyncThunk<
  DoLoginThunkResult,
  DoLoginThunkArgs,
  { state: RootState; rejectValue: string }
>("auth/doLogin", async (args, thunkAPI) => {
  const {
    email,
    useCampusTokenFallback = false,
    maxRetries = 0,
    retryDelays = [3000, 1500],
    persistIdAP = false,
  } = args;

  if (!email || email.trim() === "") {
    return thunkAPI.rejectWithValue("Email is required");
  }

  let response: LoginResponse | null = null;
  let isCampus = false;
  let lastError: any = null;

  /* ---------------------- 1) Intento con campus token ---------------------- */
  const campusToken = useCampusTokenFallback ? localStorage.getItem("campusToken") : null;
  if (campusToken) {
    try {
      const campusRes = await doCampusAuth(campusToken);
      if (campusRes.status === 200 && !campusRes.data.error) {
        response = campusRes;
        isCampus = true;
        localStorage.setItem("campusToken", campusToken);
      } else {
        console.warn("⚠️ doCampusAuth no exitoso, fallback a login estándar");
      }
    } catch (error) {
      console.error("Error authenticating with campus token:", error);
    }
  }

  /* --------------------- 2) Login estándar con reintentos -------------------- */
  if (!response) {
    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      try {
        const r = await login(email);
        response = r;
        if (r.status === 200) break;
        lastError = new Error(`Login failed with status: ${r.status}`);
      } catch (error: any) {
        lastError = error;
        response = buildEmptyLoginResponse(error);
      }

      if (attempt < maxRetries) {
        const delayMs = retryDelays[Math.min(attempt, retryDelays.length - 1)] ?? 1500;
        console.log(
          `🔄 Reintentando login (intento ${attempt + 1}/${maxRetries}) después de ${delayMs}ms...`,
        );
        await wait(delayMs);
      }
    }
  }

  if (!response) {
    return thunkAPI.rejectWithValue(lastError?.message || "Login failed");
  }

  /* ------------------------ 3) Información derivada ------------------------ */
  const { carreras, cantidadCarreras } = response.data;
  let nombreCarreraSeleccionada = "";
  let idAlumnoPrograma: number | undefined;

  if (carreras && carreras.length > 0) {
    idAlumnoPrograma = carreras[0]?.idAlumnoPrograma;

    if (cantidadCarreras > 1) {
      const idAP = localStorage.getItem("idAP");
      if (idAP) {
        nombreCarreraSeleccionada =
          carreras.find((c: any) => c.idAlumnoPrograma === Number(idAP))?.descripcionCarrera || "";
      } else {
        nombreCarreraSeleccionada = carreras[0]?.descripcionCarrera || "";
      }
    } else {
      nombreCarreraSeleccionada = carreras[0]?.descripcionCarrera || "";
    }
  }

  /* ------------------------- 4) Side-effects locales ------------------------ */
  if (response.data.token) {
    localStorage.setItem("token", response.data.token);
  }

  if (persistIdAP && idAlumnoPrograma !== undefined && carreras?.length > 0) {
    if (cantidadCarreras > 1) {
      const idAP = localStorage.getItem("idAP");
      if (!idAP) {
        localStorage.setItem("idAP", JSON.stringify(idAlumnoPrograma));
      }
    } else {
      localStorage.setItem("idAP", JSON.stringify(idAlumnoPrograma));
    }
  }

  return {
    data: response.data,
    status: response.status,
    nombreCarreraSeleccionada,
    idAlumnoPrograma,
    isCampus,
  };
});

/* ------------------------------------------------------------------------------------------------ */
/*                            Builders para registrar en cada slice                                  */
/* ------------------------------------------------------------------------------------------------ */

/**
 * Aplica al `studentSlice` los cambios de estado comunes derivados del login:
 * token, bloqueo, carreras, carrera actual (si es mono-carrera) y mostrarHorarios.
 *
 * IMPORTANTE: NO se modifica `studentInitState` desde acá. Esa transición es
 * responsabilidad de:
 *   - `App.tsx#checkUserInitAndGetStudentData` (consulta a /api/estadoAlumno)
 *   - `useAuth.ingresarEnApp` / `useAuth.ingresarEnAppMulticarrera`
 *
 * Si lo seteáramos a 2 prematuramente, el flujo multicarrera quedaría trabado
 * porque los `useEffect`s de App.tsx no detectarían el cambio 2→2 al elegir
 * carrera y `getUserId()` nunca se invocaría → `student.id` queda en 0 y la
 * pantalla queda colgada con el loader.
 *
 * Tampoco se actualiza `debeAlertarSuperposicion` ni
 * `debeFiltrarCursosConCupoNoAsignado` cuando vienen `false`, para preservar el
 * comportamiento previo de `doLoginAndCheckBloqueos` que sólo seteaba el primero
 * cuando era truthy y nunca tocaba el segundo.
 */
export const doLoginStudentBuilder = (builder: ActionReducerMapBuilder<StudentState>) => {
  builder.addCase(doLoginThunk.fulfilled, (state, action) => {
    const { data, nombreCarreraSeleccionada } = action.payload;
    const {
      token,
      carreras,
      bloqueado,
      mensajeBloqueo,
      contactoBloqueo,
      mostrarHorarios,
      mostrarAulas,
      debeAlertarSuperposicion,
      filtrarCursosConCupoNoAsignado,
      error,
    } = data;

    if (token) {
      state.token = token;
    }

    if (error) {
      // Sólo se persiste el token (que en este caso será "") y se delega el manejo del
      // error a parametersSlice (serverError). El resto del estado del student no se toca.
      return;
    }

    state.bloqueoActivo = !!bloqueado;
    state.bloqueoMensaje = mensajeBloqueo ?? "";
    state.bloqueoSubmensaje = contactoBloqueo ?? "";

    state.debeAlertarSuperposicion = !!debeAlertarSuperposicion;
    state.debeFiltrarCursosConCupoNoAsignado = !!filtrarCursosConCupoNoAsignado;

    if (Array.isArray(carreras)) {
      state.listadoCarreras = carreras;
    }

    if (nombreCarreraSeleccionada && carreras?.length === 1) {
      state.carreraActual = nombreCarreraSeleccionada;
      localStorage.setItem("carreraActual", nombreCarreraSeleccionada);
    }

    if (mostrarHorarios !== null && mostrarHorarios !== undefined) {
      state.mostrarHorarios = mostrarHorarios;
    }

    if (typeof mostrarAulas === "boolean") {
      state.mostrarAulas = mostrarAulas;
    }
  });
};

/**
 * Aplica al `parametersSlice` el flag `serverError` cuando la respuesta del login
 * llega con `error` inesperado. Los 401/404 (credenciales, whitelist IPO, usuario
 * desconocido) los maneja `useAuth.handleAuthResponse` con toasts específicos.
 */
export const doLoginParametersBuilder = (builder: ActionReducerMapBuilder<ParametersState>) => {
  builder.addCase(doLoginThunk.fulfilled, (state, action) => {
    const error = action.payload.data.error;
    if (error && !isExpectedLoginAuthError(error)) {
      state.serverError = true;
    }
  });
};

/**
 * Aplica al `globalFlagsSlice` el toggle automático de `isLogginProcess` en función
 * del ciclo de vida del thunk (pending → true, fulfilled/rejected → false).
 */
export const doLoginGlobalFlagsBuilder = <S extends GlobalFlagsLikeState>(
  builder: ActionReducerMapBuilder<S>,
) => {
  builder
    .addCase(doLoginThunk.pending, (state) => {
      state.isLogginProcess = true;
    })
    .addCase(doLoginThunk.fulfilled, (state) => {
      state.isLogginProcess = false;
    })
    .addCase(doLoginThunk.rejected, (state) => {
      state.isLogginProcess = false;
    });
};
